// THE EARLY CONTEXT (R8d criterion, boot): the WebGL2 context three would make
// in main.js boot's renderer task, made earlier — one GL call per task while
// boot waits on the network (welcome, the world manifest, meta).
//
// Why: the recent probe profiles put ~500 ms of the ONE `boot` long task
// (860 ms) into sync GL round trips — canvas.getContext (60–150 ms) and
// ~15 getExtension calls (three's WebGLExtensions.init, KTX2Loader
// detectSupport, compileq askDevice, device.js rendererString; each enable
// waits on the GPU process). Made here first, an extension three asks for
// later is the browser's cached object: no round trip in the boot task.
//
// Same output: the canvas is three's own (createCanvasElement), the attributes
// are exactly what three r170 passes for main.js's RENDERER_PARAMS
// (contextAttrs; alpha true — three tracks the param apart), and EARLY_EXT
// holds only extensions boot requests anyway, in three's own fallback chains
// (check-mapfirst §3d pins both against the three source). The one difference
// three sees — a passed context makes WebGLBackground read alpha from the
// context (true → clear alpha 0) instead of the param (false → 1) — main.js
// undoes with renderer.setClearAlpha(1) (no GL call: WebGLState starts at
// clear (0, 0, 0, 1)).
//
// The steps wait for the boot GPU probe (its own context; a first visit
// only): never timed against this context's start-up. Boot never waits for
// them: finish() runs what is left synchronously (today's cost, at most).
// No DOM at module scope (Node: the gate).
//
// THE SIZE (R8d boot steps): a canvas width / height write on a live context
// is a drawing-buffer reshape (the 2026-10-31 boot profile: setSize 113 ms +
// setPixelRatio's setSize 124 ms in the boot task). Step 0 sizes the canvas
// to its final drawing buffer BEFORE getContext (no context yet: a plain
// attribute); sizeRenderer then leaves the canvas alone when three's state
// already is the final one (a 1:1 screen: three reads its size off that
// canvas), else ONE setDrawingBufferSize — the end state of today's
// setSize + setPixelRatio either way.
import { createCanvasElement, Vector2, Vector4 } from 'three';
import { act } from './sched.js';

// the renderer's size: main.js's window size at its pixel ratio (≤ 2)
export const drawSize = () => ({ w: innerWidth, h: innerHeight, pr: Math.min(devicePixelRatio, 2) });

// == renderer.setSize(w, h) then renderer.setPixelRatio(pr) (three r170: _width / _height /
// _pixelRatio, the canvas at floor(w·pr) × floor(h·pr), its style w × h px, the viewport 0, 0, w, h),
// without a canvas write when three already holds exactly that. Returns whether it wrote the canvas.
// (three's scissor box, never set by the client — no setScissorTest — starts at the canvas's
// construction size: inert while the scissor test is off, the raw GL passes set their own)
const _sz = new Vector2(), _vp = new Vector4();
export function sizeRenderer(renderer, { w, h, pr } = drawSize()) {
  const cv = renderer.domElement;
  cv.style.width = w + 'px';
  cv.style.height = h + 'px';
  renderer.getSize(_sz); renderer.getViewport(_vp);
  if (renderer.getPixelRatio() === pr && _sz.x === w && _sz.y === h && cv.width === Math.floor(w * pr) && cv.height === Math.floor(h * pr)
    && _vp.x === 0 && _vp.y === 0 && _vp.z === w && _vp.w === h) return false;
  renderer.setDrawingBufferSize(w, h, pr);
  return true;
}

// main.js's WebGLRenderer parameters (the renderer task spreads them)
export const RENDERER_PARAMS = Object.freeze({ antialias: true, powerPreference: 'high-performance' });

// three r170 WebGLRenderer: its parameter defaults and the attributes object
// it hands canvas.getContext('webgl2', …) (alpha: true always)
export function contextAttrs(p = RENDERER_PARAMS) {
  const {
    depth = true, stencil = false, antialias = false, premultipliedAlpha = true,
    preserveDrawingBuffer = false, powerPreference = 'default', failIfMajorPerformanceCaveat = false,
  } = p;
  return { alpha: true, depth, stencil, antialias, premultipliedAlpha, preserveDrawingBuffer, powerPreference, failIfMajorPerformanceCaveat };
}

// every extension the boot task requests, as three requests it: a chain
// stops at its first supported name (three's WebGLExtensions.getExtension)
export const EARLY_EXT = Object.freeze([
  // three WebGLExtensions.init (the renderer constructor)
  ['EXT_color_buffer_float'], ['WEBGL_clip_cull_distance'], ['OES_texture_float_linear'],
  ['EXT_color_buffer_half_float'], ['WEBGL_multisampled_render_to_texture'], ['WEBGL_render_shared_exponent'],
  // compileq askDevice: getMaxAnisotropy, the parallel link
  ['EXT_texture_filter_anisotropic', 'MOZ_EXT_texture_filter_anisotropic', 'WEBKIT_EXT_texture_filter_anisotropic'],
  ['KHR_parallel_shader_compile'],
  // KTX2Loader.detectSupport (main.js boot, every tier)
  ['WEBGL_compressed_texture_astc'], ['WEBGL_compressed_texture_etc1'], ['WEBGL_compressed_texture_etc'],
  ['WEBGL_compressed_texture_s3tc', 'MOZ_WEBGL_compressed_texture_s3tc', 'WEBKIT_WEBGL_compressed_texture_s3tc'],
  ['EXT_texture_compression_bptc'],
  ['WEBGL_compressed_texture_pvrtc', 'WEBKIT_WEBGL_compressed_texture_pvrtc'],
  // device.js rendererString (detectDevice, initDetailQuality)
  ['WEBGL_debug_renderer_info'],
].map((c) => Object.freeze(c)));

const noLoss = (e) => e.preventDefault();   // a loss before three: restorable (three re-asks then)

// Start the early context: step 0 = canvas (sized) + getContext, then one extension
// chain per step, each its own task (`later`), after `probe` settles.
// finish() (the renderer task): the rest synchronously → { canvas, context }
// for the WebGLRenderer, or null (no context / lost: three makes its own, as before).
export function earlyContext({ probe = null, params = RENDERER_PARAMS, canvas: mk = createCanvasElement,
  later = (fn) => setTimeout(fn, 0), size = drawSize } = {}) {
  let canvas = null, gl = null, k = 0, done = false;
  const steps = [
    () => {
      canvas = mk();
      const s = size();   // the final drawing buffer, before the context (THE SIZE)
      canvas.width = Math.floor(s.w * s.pr);
      canvas.height = Math.floor(s.h * s.pr);
      canvas.addEventListener('webglcontextlost', noLoss, false);
      gl = canvas.getContext('webgl2', contextAttrs(params));
    },
    ...EARLY_EXT.map((chain) => () => { for (const n of chain) if (gl.getExtension(n)) break; }),
  ];
  const one = () => {
    if (k > 0 && (!gl || gl.isContextLost())) { k = steps.length; return; }   // nothing to warm
    steps[k++]();
  };
  const run = () => {
    if (done || k >= steps.length) return;
    act('boot.gl', one);
    if (k < steps.length) later(run);
  };
  Promise.resolve(probe).catch(() => null).then(() => { if (!done) later(run); });
  return {
    finish() {
      done = true;
      while (k < steps.length) one();
      if (canvas) canvas.removeEventListener('webglcontextlost', noLoss, false);
      return gl && !gl.isContextLost() ? { canvas, context: gl } : null;
    },
    // steps run so far / of (the gate; __vr)
    state: () => ({ step: k, of: steps.length, done }),
  };
}
